import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DocumentTextIcon, 
  ArrowDownRightIcon, 
  ArrowUpRightIcon,
  PhotoIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { transactionApi } from '../../api/transactionApi';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await transactionApi.getAllTransactions();
      setTransactions(response.data.data);
    } catch (error) {
      console.error('Error fetching all transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full pb-10 max-w-7xl mx-auto space-y-8">
      
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white dark:bg-gray-800 p-6 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-700 transition-colors">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">All Transactions</h1>
          <p className="text-[15px] font-medium text-gray-500 dark:text-gray-400 mt-1">
            Complete history of all customer ledgers. Click any row to edit.
          </p>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 text-[#dc343b] dark:text-red-400 px-4 py-2 rounded-xl font-bold text-[14px]">
          {transactions.length} Total Entries
        </div>
      </div>

      {/* Transactions Table Area */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] overflow-hidden transition-colors">
        {loading ? (
          <div className="p-20 flex flex-col items-center justify-center space-y-4">
            <div className="animate-spin rounded-full h-10 w-10 border-b-4 border-[#dc343b]"></div>
            <p className="text-gray-400 dark:text-gray-500 font-bold text-sm tracking-widest uppercase">Loading...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {transactions.length > 0 ? (
              <table className="w-full text-left border-collapse min-w-[800px]">
                <thead>
                  <tr className="bg-gray-50/80 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700 uppercase tracking-wider text-[11px] font-extrabold text-gray-400 dark:text-gray-500">
                    <th className="px-6 py-5 rounded-tl-3xl">Date</th>
                    <th className="px-6 py-5">Customer</th>
                    <th className="px-6 py-5">Type</th>
                    <th className="px-6 py-5">Details</th>
                    <th className="px-6 py-5 text-right">Amount</th>
                    <th className="px-6 py-5 rounded-tr-3xl"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100/80 dark:divide-gray-700/50">
                  {transactions.map((tx) => {
                    const isCredit = tx.type === 'credit';
                    return (
                      <tr 
                        key={tx._id} 
                        onClick={() => navigate(`/customers/${tx.customerId?._id}/transaction?type=${tx.type}&edit=true&transactionId=${tx._id}&from=transactions`)}
                        className="group hover:bg-gray-50/60 dark:hover:bg-gray-700/30 transition-colors cursor-pointer"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-[14px] font-bold text-gray-900 dark:text-white">
                            {new Date(tx.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                          <p className="text-[12px] font-medium text-gray-400 dark:text-gray-500 mt-0.5">
                            {new Date(tx.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </td>
                        
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 shrink-0 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-300 font-bold text-[14px] shadow-sm">
                              {tx.customerId?.name?.charAt(0).toUpperCase() || '?'}
                            </div>
                            <div>
                              <p className="text-[14px] font-bold text-gray-900 dark:text-white">{tx.customerId?.name || 'Unknown Customer'}</p>
                              <p className="text-[12px] font-medium text-gray-500 dark:text-gray-400">{tx.customerId?.phone || 'No phone'}</p>
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-extrabold capitalize ${
                            isCredit ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400' : 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                          }`}>
                            {isCredit ? <ArrowUpRightIcon className="h-3 w-3" strokeWidth={3} /> : <ArrowDownRightIcon className="h-3 w-3" strokeWidth={3} />}
                            {tx.type}
                          </div>
                        </td>

                        <td className="px-6 py-4 max-w-xs">
                          <div className="flex items-center gap-2">
                            <p className="text-[13px] font-medium text-gray-700 dark:text-gray-300 truncate">
                              {tx.description || <span className="text-gray-400 dark:text-gray-500 italic">No details</span>}
                            </p>
                            {tx.attachments && tx.attachments.length > 0 && (
                              <div className="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 p-1.5 rounded-full flex-shrink-0" title="Has Attachments">
                                {tx.attachments[0].type === 'pdf' ? (
                                  <DocumentTextIcon className="h-3.5 w-3.5" />
                                ) : (
                                  <PhotoIcon className="h-3.5 w-3.5" />
                                )}
                              </div>
                            )}
                          </div>
                        </td>

                        <td className="px-6 py-4 text-right whitespace-nowrap">
                          <span className={`text-[16px] font-extrabold ${isCredit ? 'text-red-500 dark:text-red-400' : 'text-green-600 dark:text-green-500'}`}>
                            ₹{tx.amount.toLocaleString()}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 shadow-sm p-2 rounded-xl text-gray-400 dark:text-gray-300 hover:text-[#dc343b] dark:hover:text-red-400 hover:border-red-100 dark:hover:border-red-900 hover:bg-red-50 dark:hover:bg-red-900/40 transition-all">
                              <PencilIcon className="h-4 w-4" />
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="p-20 text-center flex flex-col items-center">
                <div className="h-20 w-20 bg-gray-50 dark:bg-gray-700/50 rounded-full flex items-center justify-center mb-5 shadow-inner">
                  <DocumentTextIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
                </div>
                <h3 className="text-gray-900 dark:text-white font-extrabold text-xl">No transactions found</h3>
                <p className="text-gray-500 dark:text-gray-400 text-[14px] mt-2 max-w-sm">
                  You haven't recorded any transactions yet. Your complete transaction history will appear here.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
};

export default Transactions;
